import { useEffect, useState } from "react";
import {
  Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { api, ApiError } from "../api";
import type {
  DashboardKPI, KwPoint, Period, StatsBundle, TurbineDashboardStatus,
} from "../types";
import { tg } from "../telegram";
import { useT } from "../i18n";

function KpiCard({ label, value, sub, variant }: {
  label: string; value: string | number; sub?: string;
  variant?: "success" | "danger" | "warning" | "neutral";
}) {
  return (
    <div className={`kpi-card ${variant ?? "neutral"}`}>
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      {sub && <div className="sub">{sub}</div>}
    </div>
  );
}

// Format one number for the KW / KV tile.
// Integers stay as-is ("1608"), fractionals get one decimal ("19.5").
function fmtNum(v: number): string {
  if (!Number.isFinite(v)) return "—";
  return Number.isInteger(v) ? v.toString() : v.toFixed(1);
}

// Two-value tile: min and max side by side, each labeled below.
// Used for the KW & KV dashboard cards.
// When `turbineName` and `onCycle` are provided, the card becomes clickable —
// tapping cycles to the next turbine and the name shows in the header.
function KpiRangeCard({ label, min, max, minTag, maxTag, turbineName, onCycle }: {
  label: string;
  min: number | null;
  max: number | null;
  minTag: string;
  maxTag: string;
  turbineName?: string;
  onCycle?: () => void;
}) {
  const hasData = min !== null && max !== null;
  const clickable = !!onCycle;
  return (
    <div
      className={`kpi-card neutral${clickable ? " clickable" : ""}`}
      onClick={onCycle}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onCycle?.();
        }
      } : undefined}
    >
      <div className="kpi-card-head">
        <div className="label">{label}</div>
        {turbineName && (
          <div className="kpi-card-turbine">
            {turbineName}
            <span className="kpi-card-cycle" aria-hidden="true">⇄</span>
          </div>
        )}
      </div>
      {hasData ? (
        <div className="kpi-range">
          <div className="kpi-range-col">
            <div className="kpi-range-value">{fmtNum(min!)}</div>
            <div className="kpi-range-tag">{minTag}</div>
          </div>
          <div className="kpi-range-col">
            <div className="kpi-range-value">{fmtNum(max!)}</div>
            <div className="kpi-range-tag">{maxTag}</div>
          </div>
        </div>
      ) : (
        <div className="value">—</div>
      )}
    </div>
  );
}

function Skeleton() {
  return (
    <>
      <div className="card skeleton" style={{ height: 110 }} />
      <div className="kpi-grid">
        {Array(4).fill(0).map((_, i) => (
          <div key={i} className="kpi-card skeleton" style={{ height: 86 }} />
        ))}
      </div>
      <div className="card skeleton" style={{ height: 180 }} />
    </>
  );
}

// Human-readable duration: "2 ч 15 мин" / "45 мин" / "—".
function fmtDuration(sec: number, t: (k: string, p?: Record<string, string | number>) => string): string {
  if (!sec || sec <= 0) return t("dashboard.turbines.no_downtime");
  const total = Math.round(sec);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const parts: string[] = [];
  if (days > 0) parts.push(`${days} ${t("dashboard.turbines.unit_d")}`);
  if (hours > 0) parts.push(`${hours} ${t("dashboard.turbines.unit_h")}`);
  if (mins > 0 || (days === 0 && hours === 0)) parts.push(`${mins} ${t("dashboard.turbines.unit_min")}`);
  return parts.join(" ");
}

// Relative "X ago" for the last-reading timestamp.
function fmtAgo(iso: string | null, t: (k: string, p?: Record<string, string | number>) => string): string {
  if (!iso) return t("dashboard.turbines.no_data");
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return t("dashboard.turbines.just_now");
  if (diff < 3600) return `${Math.floor(diff / 60)} ${t("dashboard.turbines.unit_min")} ${t("dashboard.turbines.ago")}`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} ${t("dashboard.turbines.unit_h")} ${t("dashboard.turbines.ago")}`;
  return `${Math.floor(diff / 86400)} ${t("dashboard.turbines.unit_d")} ${t("dashboard.turbines.ago")}`;
}

function TurbinesStatusGrid({ turbines }: { turbines: TurbineDashboardStatus[] }) {
  const { t } = useT();
  if (turbines.length === 0) return null;
  return (
    <div className="turbines-status-grid">
      {turbines.map(tb => {
        const stateCls = !tb.is_active ? "off" : tb.is_running ? "running" : "stopped";
        const stateLabel = !tb.is_active
          ? t("turbines.state_off")
          : tb.is_running ? t("turbines.state_running") : t("turbines.state_stopped");
        const stateIcon = !tb.is_active ? "⛔" : tb.is_running ? "🟢" : "❌";
        return (
          <div key={tb.id} className={`turbine-status-mini ${stateCls}`}>
            <div className="turbine-status-mini-head">
              <span className="turbine-status-mini-name">{tb.name}</span>
              <span className="turbine-status-mini-state">{stateIcon} {stateLabel}</span>
            </div>
            <div className="turbine-status-mini-row">
              <div>
                <div className="turbine-status-mini-label">KW</div>
                <div className="turbine-status-mini-val">
                  {tb.last_kw !== null ? fmtNum(tb.last_kw) : "—"}
                </div>
              </div>
              <div>
                <div className="turbine-status-mini-label">{t("dashboard.turbines.downtime")}</div>
                <div className="turbine-status-mini-val small">
                  {fmtDuration(tb.downtime_sec, t)}
                </div>
              </div>
            </div>
            <div className="turbine-status-mini-foot">
              {t("dashboard.turbines.last_reading")}: {fmtAgo(tb.last_at, t)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function KwSparklineCard({ points }: { points: KwPoint[] }) {
  const { t } = useT();
  if (points.length === 0) return null;
  const data = points.map(p => ({
    t: p.t,
    kw: p.kw,
    label: new Date(p.t).toLocaleString(undefined, {
      day: "2-digit", month: "2-digit",
      hour: "2-digit", minute: "2-digit",
    }),
  }));
  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>{t("dashboard.sparkline.title")}</h2>
      <div style={{ fontSize: 12, color: "var(--hint)", marginBottom: 8 }}>
        {t("dashboard.sparkline.hint")}
      </div>
      <ResponsiveContainer width="100%" height={120}>
        <AreaChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="kwSparkFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#2481cc" stopOpacity={0.45} />
              <stop offset="100%" stopColor="#2481cc" stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis dataKey="label" tick={{ fontSize: 9 }} minTickGap={40} />
          <YAxis tick={{ fontSize: 9 }} width={36} />
          <Tooltip
            formatter={(v: number | string) => [`${v} KW`, ""]}
            labelStyle={{ fontSize: 11 }}
            contentStyle={{ fontSize: 12, background: "var(--bg2)", border: "1px solid var(--border)" }}
          />
          <Area
            type="monotone"
            dataKey="kw"
            stroke="#2481cc"
            strokeWidth={2}
            fill="url(#kwSparkFill)"
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function heatmapColor(pct: number, hasData: boolean): string {
  if (!hasData) return "rgba(127,127,127,0.08)";
  if (pct < 50) return `rgba(255, 80, 90, ${0.30 + pct/100 * 0.45})`;
  if (pct < 80) return `rgba(255, 170, 0, ${0.30 + (pct-50)/30 * 0.45})`;
  return `rgba(52, 199, 89, ${0.35 + (pct-80)/20 * 0.50})`;
}

// Count-based heatmap (free-form model — no "% completion"). Intensity = count.
function countHeatColor(count: number): string {
  if (count <= 0) return "rgba(127,127,127,0.08)";
  // Cap at 5+ for full opacity.
  const t = Math.min(count / 5, 1);
  return `rgba(52, 199, 89, ${0.18 + t * 0.45})`;
}

export default function Dashboard() {
  const { t } = useT();
  const [period, setPeriod] = useState<Period>("today");
  const [kpi, setKpi] = useState<DashboardKPI | null>(null);
  const [stats, setStats] = useState<StatsBundle | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Which turbine the KW / KV tiles currently mirror. Initialised lazily
  // from kpi.turbines on first load; tap on a tile cycles to the next.
  const [selectedTurbineId, setSelectedTurbineId] = useState<number | null>(null);

  const PERIODS: { key: Period; label: string }[] = [
    { key: "today", label: t("period.today") },
    { key: "yest",  label: t("period.yest") },
    { key: "7d",    label: t("period.7d") },
    { key: "30d",   label: t("period.30d") },
    { key: "all",   label: t("period.all") },
  ];

  const reload = () => {
    setError(null);
    setLoading(true);
    Promise.all([
      api.get<DashboardKPI>(`/api/dashboard?period=${period}`),
      api.get<StatsBundle>(`/api/stats?period=${period}`),
    ])
      .then(([k, s]) => {
        setKpi(k);
        setStats(s);
        // Pick the first turbine the first time we see data, or if the
        // previously-selected turbine vanished.
        if (k.turbines.length > 0) {
          setSelectedTurbineId(prev =>
            prev !== null && k.turbines.some(t => t.id === prev) ? prev : k.turbines[0].id
          );
        }
      })
      .catch((e: ApiError) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(reload, [period]);

  const changePeriod = (p: Period) => {
    tg()?.HapticFeedback?.impactOccurred("light");
    setPeriod(p);
  };

  // Cycle the KW/KV tiles to the next turbine. Both tiles always show the
  // same turbine, so a tap on either advances the shared selection.
  const cycleTurbine = () => {
    if (!kpi || kpi.turbines.length < 2) return;
    tg()?.HapticFeedback?.selectionChanged?.();
    const idx = kpi.turbines.findIndex(t => t.id === selectedTurbineId);
    const next = kpi.turbines[(idx + 1) % kpi.turbines.length];
    setSelectedTurbineId(next.id);
  };

  const selectedTurbine = kpi?.turbines.find(t => t.id === selectedTurbineId) ?? null;

  return (
    <div>
      <h1>{t("dashboard.title")}</h1>

      <div className="period-tabs">
        {PERIODS.map(p => (
          <button key={p.key}
            className={"period-tab" + (p.key === period ? " active" : "")}
            onClick={() => changePeriod(p.key)}>{p.label}</button>
        ))}
      </div>

      {error && (
        <div className="alert error">
          <div style={{ marginBottom: 8 }}>⚠ {error}</div>
          <button className="btn secondary" onClick={reload}>{t("common.retry")}</button>
        </div>
      )}

      {loading && !kpi && <Skeleton />}

      {kpi && (
        <>
          <TurbinesStatusGrid turbines={kpi.turbines} />

          <div className="kpi-grid">
            <KpiCard label={t("dashboard.kpi.completed")}
              value={kpi.completed}
              variant="success" />
            <KpiRangeCard label="KW"
              min={selectedTurbine ? selectedTurbine.kw_min : kpi.kw_min}
              max={selectedTurbine ? selectedTurbine.kw_max : kpi.kw_max}
              minTag={t("dashboard.kpi.min")} maxTag={t("dashboard.kpi.max")}
              turbineName={selectedTurbine?.name}
              onCycle={kpi.turbines.length > 1 ? cycleTurbine : undefined}
            />
            <KpiCard label={t("dashboard.kpi.emergency")} value={kpi.emergency} variant={kpi.emergency > 0 ? "danger" : "neutral"} />
            <KpiRangeCard label="KV"
              min={selectedTurbine ? selectedTurbine.kv_min : kpi.kv_min}
              max={selectedTurbine ? selectedTurbine.kv_max : kpi.kv_max}
              minTag={t("dashboard.kpi.min")} maxTag={t("dashboard.kpi.max")}
              turbineName={selectedTurbine?.name}
              onCycle={kpi.turbines.length > 1 ? cycleTurbine : undefined}
            />
          </div>

          <KwSparklineCard points={kpi.kw_sparkline} />

          {stats && (
            <>
              {stats.by_hour.some(h => h.total > 0) && (
                <div className="card">
                  <h2>{t("dashboard.by_hour.title")}</h2>
                  <div style={{ fontSize: 11, color: "var(--hint)", marginBottom: 8 }}>
                    {t("dashboard.by_hour.legend_count")}
                  </div>
                  {[0, 12].map(offset => (
                    <div key={offset} className="heatmap-row">
                      {stats.by_hour.slice(offset, offset + 12).map(h => {
                        const tooltip = h.total === 0
                          ? `${h.hour.toString().padStart(2, "0")}:00 — ${t("dashboard.by_hour.no_data")}`
                          : `${h.hour.toString().padStart(2, "0")}:00 — ${h.completed} / ${h.total}`;
                        return (
                          <div key={h.hour} title={tooltip}
                            className="heat-cell"
                            style={{ background: countHeatColor(h.total) }}>
                            <div className="heat-hour">{h.hour}</div>
                            {h.total > 0 && (
                              <div className="heat-pct">{h.total}</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}

              {stats.by_user.length > 0 && (
                <div className="card">
                  <h2>{t("dashboard.users.title")}</h2>
                  <table className="table" style={{ fontSize: 12 }}>
                    <thead>
                      <tr>
                        <th>{t("dashboard.users.col_name")}</th>
                        <th style={{ textAlign: "right" }}>✅</th>
                        <th style={{ textAlign: "right" }}>🚨</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.by_user.map(u => (
                        <tr key={u.user_id}>
                          <td>{u.full_name}</td>
                          <td style={{ textAlign: "right", color: "#34c759" }}>{u.completed}</td>
                          <td style={{ textAlign: "right", color: u.emergency > 0 ? "#ff3b30" : "var(--hint)" }}>{u.emergency}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {kpi.total_slots === 0 && (
                <div className="empty-state">
                  {t("dashboard.empty")}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
